import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ShoppingCart,
  Search,
  Package,
  ArrowLeft,
  Plus,
  Filter,
  Truck,
  ShieldCheck,
  Globe,
  X,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import { useCart } from '../contexts/CartContext';
import Navbar from './Navbar';
import Footer from './Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const resolveImg = (src) => {
  if (!src) return '';
  if (src.startsWith('http')) return src;
  return `${BACKEND_URL}${src}`;
};

const ToolStore = () => {
  const { addItem, totalItems, currency, setCurrency, symbol } = useCart();
  const navigate = useNavigate();
  const [tools, setTools] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [search, setSearch] = useState('');
  const [showBanner, setShowBanner] = useState(
    () => localStorage.getItem('store_banner_dismissed') !== '1'
  );

  const dismissBanner = () => {
    setShowBanner(false);
    localStorage.setItem('store_banner_dismissed', '1');
  };

  useEffect(() => {
    let mounted = true;
    Promise.all([
      axios.get(`${API}/tools/`),
      axios.get(`${API}/tools/categories`),
    ])
      .then(([toolsRes, catRes]) => {
        if (!mounted) return;
        setTools(toolsRes.data || []);
        setCategories(catRes.data?.categories || []);
      })
      .catch((err) => {
        console.error('Error loading tools:', err);
        toast.error('No se pudieron cargar las herramientas');
      })
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  const priceField = currency === 'eur' ? 'price_eur' : 'price_usd';

  const filteredTools = useMemo(() => {
    let list = tools;
    if (activeCategory !== 'all') {
      list = list.filter((t) => t.category === activeCategory);
    }
    if (search.trim()) {
      const q = search.toLowerCase().trim();
      list = list.filter(
        (t) => t.name.toLowerCase().includes(q) || t.description.toLowerCase().includes(q)
      );
    }
    return list;
  }, [tools, activeCategory, search]);

  const handleAdd = (tool) => {
    addItem(tool, 1);
    toast.success(`${tool.name} añadido al carrito`, { duration: 2000 });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-red-50">
      <Navbar />

      <div className="pt-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        {/* Promo banner */}
        {showBanner && (
          <div
            data-testid="store-promo-banner"
            className="relative mb-8 rounded-2xl bg-gradient-to-r from-red-600 via-red-600 to-red-700 text-white px-5 sm:px-7 py-4 shadow-lg overflow-hidden"
          >
            <div className="absolute -right-6 -top-8 w-40 h-40 bg-white/10 rounded-full blur-2xl pointer-events-none" />
            <div className="relative flex items-center gap-3 sm:gap-4 pr-8">
              <div className="hidden sm:flex w-11 h-11 rounded-full bg-white/15 items-center justify-center flex-shrink-0">
                <Truck size={22} />
              </div>
              <div>
                <p className="font-bold text-sm sm:text-base leading-snug" data-testid="store-promo-title">
                  Envío internacional · Herramientas profesionales
                </p>
                <p className="text-white/85 text-xs sm:text-sm mt-0.5">
                  Pago seguro con Stripe y cotización de envío personalizada para tu país.
                </p>
              </div>
            </div>
            <button
              data-testid="store-promo-dismiss"
              onClick={dismissBanner}
              aria-label="Cerrar"
              className="absolute top-3 right-3 text-white/80 hover:text-white transition-colors"
            >
              <X size={18} />
            </button>
          </div>
        )}

        {/* Trust strip — siempre visible */}
        <div
          data-testid="store-trust-strip"
          className="mb-8 grid grid-cols-1 sm:grid-cols-3 gap-3"
        >
          <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3">
            <Truck size={22} className="text-red-600 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-slate-900">Entrega en 12-20 días</p>
              <p className="text-xs text-slate-500">Con seguimiento incluido</p>
            </div>
          </div>
          <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3">
            <ShieldCheck size={22} className="text-red-600 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-slate-900">Pago 100% seguro</p>
              <p className="text-xs text-slate-500">Procesado con Stripe</p>
            </div>
          </div>
          <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3">
            <Globe size={22} className="text-red-600 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-slate-900">Envío internacional</p>
              <p className="text-xs text-slate-500">A España, UE y Latam</p>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <Link
              to="/"
              data-testid="store-back-home"
              className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-red-600 transition-colors mb-2"
            >
              <ArrowLeft size={14} /> Volver al inicio
            </Link>
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight">
              Tienda de <span className="text-red-600">Herramientas</span>
            </h1>
            <p className="text-slate-600 mt-2 max-w-2xl">
              Herramientas profesionales seleccionadas para drywall. Envío a todo el mundo —
              cotizamos el coste de envío tras tu compra.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex bg-white border rounded-lg overflow-hidden">
              <button
                data-testid="store-currency-usd"
                onClick={() => setCurrency('usd')}
                className={`px-3 py-2 text-sm font-semibold ${
                  currency === 'usd' ? 'bg-red-600 text-white' : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                USD ($)
              </button>
              <button
                data-testid="store-currency-eur"
                onClick={() => setCurrency('eur')}
                className={`px-3 py-2 text-sm font-semibold ${
                  currency === 'eur' ? 'bg-red-600 text-white' : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                EUR (€)
              </button>
            </div>
            <Link to="/carrito">
              <Button
                data-testid="store-cart-btn"
                className="bg-slate-900 hover:bg-slate-800 text-white relative"
              >
                <ShoppingCart size={18} className="mr-2" />
                Carrito
                {totalItems > 0 && (
                  <span
                    data-testid="store-cart-badge"
                    className="absolute -top-2 -right-2 bg-red-600 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center"
                  >
                    {totalItems}
                  </span>
                )}
              </Button>
            </Link>
          </div>
        </div>

        {/* Search + Categories */}
        <div className="mb-6 space-y-4">
          <div className="relative">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="store-search-input"
              placeholder="Buscar herramienta..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-white border-slate-200"
            />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs uppercase text-slate-500 font-semibold flex items-center gap-1">
              <Filter size={12} /> Categorías:
            </span>
            <button
              data-testid="store-cat-all"
              onClick={() => setActiveCategory('all')}
              className={`px-3 py-1.5 text-xs rounded-full font-semibold transition-colors ${
                activeCategory === 'all'
                  ? 'bg-red-600 text-white'
                  : 'bg-white text-slate-600 border border-slate-200 hover:border-red-300'
              }`}
            >
              Todas
            </button>
            {categories.map((c) => (
              <button
                key={c.id}
                data-testid={`store-cat-${c.id}`}
                onClick={() => setActiveCategory(c.id)}
                className={`px-3 py-1.5 text-xs rounded-full font-semibold transition-colors ${
                  activeCategory === c.id
                    ? 'bg-red-600 text-white'
                    : 'bg-white text-slate-600 border border-slate-200 hover:border-red-300'
                }`}
              >
                {c.name}
              </button>
            ))}
          </div>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="text-center py-16">
            <div className="animate-spin rounded-full h-10 w-10 border-4 border-red-600 border-t-transparent mx-auto" />
            <p className="text-slate-500 mt-3">Cargando catálogo...</p>
          </div>
        ) : filteredTools.length === 0 ? (
          <div className="text-center py-16">
            <Package size={56} className="text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600">No se encontraron herramientas con esos filtros.</p>
          </div>
        ) : (
          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
            data-testid="store-tools-grid"
          >
            {filteredTools.map((tool) => (
              <Card
                key={tool.id}
                data-testid={`store-tool-${tool.id}`}
                onClick={() => navigate(`/tienda/producto/${tool.id}`)}
                className="group border-0 shadow-md hover:shadow-2xl transition-shadow duration-300 overflow-hidden bg-white flex flex-col h-full cursor-pointer"
              >
                <div
                  className="aspect-[4/3] bg-slate-100 bg-cover bg-center transition-transform duration-300 group-hover:scale-[1.02]"
                  style={{ backgroundImage: `url(${resolveImg(tool.image)})` }}
                />
                <CardContent className="p-4 flex flex-col flex-1">
                  <span className="text-[10px] uppercase tracking-wide text-red-600 font-bold mb-1">
                    {tool.id}
                  </span>
                  <h3 className="text-base font-semibold text-slate-900 line-clamp-2 leading-tight min-h-[2.5rem] group-hover:text-red-600 transition-colors">
                    {tool.name}
                  </h3>
                  <p className="text-xs text-slate-500 mt-1 line-clamp-2 min-h-[2rem]">
                    {tool.description}
                  </p>
                  <div className="flex items-end justify-between mt-auto pt-3">
                    <div>
                      <p className="text-2xl font-bold text-slate-900">
                        {symbol}
                        {tool[priceField].toFixed(2)}
                      </p>
                      <p className="text-[10px] uppercase text-slate-400">
                        {currency.toUpperCase()}
                      </p>
                    </div>
                    <Button
                      data-testid={`store-add-${tool.id}`}
                      onClick={(e) => { e.stopPropagation(); handleAdd(tool); }}
                      className="bg-red-600 hover:bg-red-700 text-white"
                      size="sm"
                    >
                      <Plus size={14} className="mr-1" /> Añadir
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default ToolStore;
